import { useEffect, useMemo, useState } from 'react';
import { parseQueryFilters, removeQueryFilters } from '@/lib/utils/filter.logic';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { StanFilterConfig, StanFilterValue } from '@/components/core-components/StanFilters/types';
import { toSqlOperator } from '@/components/core-components/StanFilters/utils/filterMapping';

const addFiltersToQueryParams = (
    query: URLSearchParams,
    filterValues: StanFilterValue[],
    setParams: SetURLSearchParams
) => {
    filterValues.forEach(filter => {
        query.set(`${filter.id}[${filter.operator}]`, String(filter.value));
    });
    // Always set page to 1
    query.set('page', '1');
    setParams(query);
};

export function useQueryFilters({ filters }: { filters: StanFilterConfig[] }) {
    const [params, setParams] = useSearchParams();
    const fieldNames = useMemo(() => {
        return filters.map(filter => filter.id);
    }, [filters]);

    const [filterValues, setFilterValues] = useState<StanFilterValue[]>([]);
    const [backendFilterValues, setBackendFilterValues] = useState<string>();

    // Convert params to string for stable dependency comparison
    const paramsString = params.toString();

    useEffect(() => {
        // Don't parse if filters config is empty (not loaded yet)
        if (filters.length === 0) {
            setFilterValues([]);
            setBackendFilterValues(undefined);
            return;
        }

        const newFilterValues = parseQueryFilters(params, filters);
        setFilterValues(newFilterValues);
        const newBackendFilterValues = newFilterValues.map(filter => ({
            fieldName: filter.id,
            operator: toSqlOperator(filter.operator),
            value: filter.value,
            type: filter.type,
            urlCode: filter.operator,
            ...(filter.secondValue && { secondValue: filter.secondValue }),
        }));
        setBackendFilterValues(JSON.stringify(newBackendFilterValues));
    }, [filters, paramsString]);

    const handleResetFilters = () => {
        setFilterValues([]);
        setBackendFilterValues(undefined);
        setParams();
    };

    const handleFilterChange = (filterValues: StanFilterValue[]) => {
        const newBackendFilterValues = filterValues.map(filter => ({
            fieldName: filter.id,
            operator: toSqlOperator(filter.operator),
            value: filter.value,
            type: filter.type,
            urlCode: filter.operator,
            ...(filter.secondValue && { secondValue: filter.secondValue }),
        }));
        // Remove old filters from query params
        const query = removeQueryFilters(params, fieldNames);
        // Add new filters to query params
        addFiltersToQueryParams(query, filterValues, setParams);
        setFilterValues(filterValues);
        setBackendFilterValues(JSON.stringify(newBackendFilterValues));
    };

    return {
        filterValues,
        backendFilterValues,
        handleResetFilters,
        handleFilterChange,
    };
}
